import { Component } from 'react';
import { connect } from 'react-redux';
import { getContentCssAssets } from 'ContentEditorUI/redux/selectors/contentAssetsSelectors';
import { assetsAreEqual, convertDomElementToAssetJSON } from 'ContentEditorUI/utils/contentAssetUtils';
import { CONTENT_ASSET_TYPE_CSS, CONTENT_ASSET_TYPE_INLINE_CSS, CONTENT_ASSET_TYPE_INLINE_EDITOR_CSS, CONTENT_ASSET_TYPE_INLINE_OTHER, CONTENT_ASSET_TYPE_LINKED_ASSET, INLINE_EDITOR_CSS_ELEMENT_ID } from 'ContentEditorUI/ts/ContentAssetsTypes';
import { isEqual } from 'underscore';
import { logInpageSeleniumEvent } from 'InpageEditorUI/eventsAsActions/seleniumEventsAsActions';
class ContentCssAssetContainer extends Component {
  constructor(...args) {
    super(...args);
    this.currentRenderId = 0;
    this.currentRenderPromiseForTest = Promise.resolve();
  } // Promise instance that is swapped out each time the
  // swapOutAllTheStyles method is called. Will resolve when
  // the removal of old elements is complete.
  componentDidMount() {
    // In some edge cases (namely GCE "module only" and "group only" modes)
    // there can be some CSS output by the renderer, but because we couldn't fetch
    // from the css assets endpoint due to lack of a content id, we end up
    // with an empty array of css assets. In those cases, let's just not touch
    // any css on the page, and let the initial css from the renderer persist
    // instead of removing it.
    if (this.props.cssAssets.length === 0) {
      return;
    }
    this.swapOutAllTheStyles();
  }
  componentDidUpdate(prevProps) {
    const {
      cssAssets
    } = this.props;
    if (!isEqual(prevProps.cssAssets, cssAssets)) {
      this.swapOutAllTheStyles();
    }
  }

  // Heads up, this method _mutates_ the cssAssets array by adding an element property to the matching
  // css asset json if it's found
  analyzeExistingStyleElementsAndMutateCssAssetsArray({
    beginStylesMeta,
    endStylesMeta,
    cssAssets
  }) {
    const oldStyleElementsToRemove = [];
    let editorGeneratedStylesElement;
    // Get references to all the existing style element nodes
    let currentElement = beginStylesMeta.nextElementSibling;
    while (currentElement && !currentElement.isSameNode(endStylesMeta)) {
      // Special case the style element that the editor creates / renders CSS into, get a direct
      // reference to it so we don't keep blowing it away and creating new elements
      if (currentElement.id === INLINE_EDITOR_CSS_ELEMENT_ID) {
        editorGeneratedStylesElement = currentElement;
        currentElement = currentElement.nextElementSibling;
        continue;
      }

      // Convert the element existing style element into an "asset json" format.
      const elementAsAssetJSON = convertDomElementToAssetJSON(currentElement);

      // Figure out if this existing style element, as an asset json format, matches
      // any of the assets in the css assets array, if so, assign this element to that
      // json format asset so it can be reused later when making sure the css
      // is in the right order
      const newAssetForThisElement = cssAssets.find(asset => assetsAreEqual(elementAsAssetJSON, asset));
      if (newAssetForThisElement) {
        newAssetForThisElement.element = currentElement;
      } else {
        if (currentElement.hasAttribute('data-styled') && currentElement.innerHTML === '') {
          // If the existingStyleElement has data-style attribute and __does not__ have innerHTML,then we do
          // not need to add this element to the oldStyleElementsToRemove array because it is associated to
          // a client side only island that uses styled-components. If we were to add this to the array, then
          // it will eventually get cleaned up thus breaking the styles associated to the island.
          currentElement = currentElement.nextElementSibling;
          continue;
        }
        // If we can't find an asset for this existing element, assume that it's because
        // that asset was removed (maybe the module got deleted?). Mark them for deletion.
        oldStyleElementsToRemove.push(currentElement);
      }
      currentElement = currentElement.nextElementSibling;
    }
    return {
      oldStyleElementsToRemove,
      editorGeneratedStylesElement
    };
  }
  createExternalStyleSheetElement(asset) {
    // If the asset is an external style sheet, create link tags and
    // throw on some onload handlers
    const url = asset.url;
    const newElement = document.createElement('link');
    newElement.rel = 'stylesheet';
    newElement.href = url;
    const loadPromise = new Promise(resolve => {
      newElement.onload = () => resolve();
      newElement.onerror = () => resolve();
    });
    return {
      newElement,
      loadPromise
    };
  }
  createStyleElementForSnippet(asset) {
    // If the asset is a css string, create a style block and put in
    const newElement = document.createElement('style');
    newElement.innerHTML = asset.snippet;
    return {
      newElement
    };
  }
  hydrateHtmlIntoElementsAndAppendToDom(asset, previousStyleElement) {
    // If the asset is an arbritrary inline html, hydrate it, then iterate over whatever children
    // there are and insert them where they belong
    const tempDiv = document.createElement('div');
    tempDiv.insertAdjacentHTML('afterbegin', asset.snippet);
    // A developer could put arbritrary HTML in their require_css hubl call, so we assume
    // there could be more than one here.
    while (tempDiv.firstElementChild) {
      const childElement = tempDiv.firstElementChild;
      previousStyleElement.insertAdjacentElement('afterend', childElement);
      previousStyleElement = childElement;
    }
    return {
      previousStyleElement
    };
  }
  moveOrCreateStyleElementsForAssets({
    beginStylesMeta,
    editorGeneratedStylesElement,
    cssAssets
  }) {
    // For any external style sheets, we want to track promises
    // that resolve when their onload events fire. We'll wait
    // for all the style load promises to resolve before removing
    // the old styles to help prevent flashes of unstyled content
    const newStyleLoadPromises = [];
    const clonedAndMovedStyleElementsToRemove = [];
    let previousStyleElement = beginStylesMeta;

    // Put the new styles in place
    cssAssets.forEach(asset => {
      // Special case the style element the editor front end generates styles for
      // Just update the existing style elements innerHTML instead of creating additional
      // churn by constantly recreating / deleting style elements
      if (asset.type === CONTENT_ASSET_TYPE_INLINE_EDITOR_CSS) {
        // If the style element is already on the page, just update it's inner html
        if (editorGeneratedStylesElement) {
          editorGeneratedStylesElement.innerHTML = asset.snippet;
          previousStyleElement = editorGeneratedStylesElement;
        } else {
          // Else this special style element doesn't exist yet, create it.
          const {
            newElement
          } = this.createStyleElementForSnippet(asset);
          newElement.id = INLINE_EDITOR_CSS_ELEMENT_ID;
          previousStyleElement.insertAdjacentElement('afterend', newElement);
          previousStyleElement = newElement;
        }
        return;
      }

      // If this asset already has a style element on the page (this element
      // property would of been set on this asset in analyzeExistingStyleElementsAndMutateCssAssetsArray)
      if (asset.element) {
        // Is that style element after the previous one already? If so, don't touch this
        // to avoid having to repaint the styles
        const isAssetElementAlreadyInRightSpot = asset.element.previousElementSibling && asset.element.previousElementSibling.isSameNode(previousStyleElement);
        if (isAssetElementAlreadyInRightSpot) {
          previousStyleElement = asset.element;
          return;
        } else {
          // Else there is already a style element for this asset, but its in the wrong order, move it

          // Generally we want to just move the a style element from one spot in the DOM to the other
          let elementToMove = asset.element;

          // However for external style sheets moving the element can cause css thrashing as the
          // parsed CSS is removed, the browser paints, then when the DOM node is reattached the DOM node
          // it must regrab the asset from cache, reparse it, and then paint again. In those cases we
          // should instead create a duplicate style element, attach onload handlers to it, and mark the
          // original style element for deletion to help prevent css thrashing.
          if (asset.type === CONTENT_ASSET_TYPE_CSS) {
            const {
              newElement,
              loadPromise
            } = this.createExternalStyleSheetElement(asset);
            elementToMove = newElement;
            newStyleLoadPromises.push(loadPromise);
            clonedAndMovedStyleElementsToRemove.push(asset.element);
          }
          previousStyleElement.insertAdjacentElement('afterend', elementToMove);
          previousStyleElement = elementToMove;
          return;
        }
      } else {
        // If there is no style element for this asset already on the page, create one for it and insert it the right spot
        let newElement;
        let loadPromise;
        if (asset.type === CONTENT_ASSET_TYPE_CSS) {
          ({
            newElement,
            loadPromise
          } = this.createExternalStyleSheetElement(asset));
          newStyleLoadPromises.push(loadPromise);
        } else if (asset.type === CONTENT_ASSET_TYPE_INLINE_CSS) {
          ({
            newElement
          } = this.createStyleElementForSnippet(asset));
        } else if (asset.type === CONTENT_ASSET_TYPE_INLINE_OTHER) {
          ({
            previousStyleElement
          } = this.hydrateHtmlIntoElementsAndAppendToDom(asset, previousStyleElement));
          // Breaking this loop early because the above method already appended
          // the new elements to the DOM.
          return;
        } else if (asset.type === CONTENT_ASSET_TYPE_LINKED_ASSET) {
          // TODO, eventually add  BE support for LINKED_ASSET?
          // Some initial FE support in `convertDomElementToAssetJSON`

          // newElement = document.createElement('link');
          // newElement.rel = asset.loadMethod;    // preload or prefetch
          // newElement.as = asset.loadAsProperty; // style or script
          // newElement.href = asset.url;

          // But for now, simply break and don't worry about supporting <link rel="preload" ...> elements
          // they are only hints to speed up loading vs necessary for the page
          return;
        } else {
          throw new Error(`Unsupported asset type: ${asset.type}`);
        }
        // Insert this new asset after the last one (which we already know is in the right spot)
        previousStyleElement.insertAdjacentElement('afterend', newElement);
        previousStyleElement = newElement;
      }
    });
    return {
      newStyleLoadPromises,
      clonedAndMovedStyleElementsToRemove
    };
  }
  swapOutAllTheStyles() {
    const {
      cssAssets,
      dispatchActionToEventBus
    } = this.props;

    // Find our marker elements
    const beginStylesMeta = document.querySelector('meta[name="hs-head-css-starts"]');
    const endStylesMeta = document.querySelector('meta[name="hs-head-css-end"]');
    if (!beginStylesMeta || !endStylesMeta) {
      console.error('Cannot update page styles because <meta name="hs-head-css-starts"/> or <meta name="hs-head-css-end"> is missing');
      return;
    }
    const {
      oldStyleElementsToRemove,
      editorGeneratedStylesElement
    } = this.analyzeExistingStyleElementsAndMutateCssAssetsArray({
      beginStylesMeta,
      endStylesMeta,
      cssAssets
    });
    const {
      newStyleLoadPromises,
      clonedAndMovedStyleElementsToRemove
    } = this.moveOrCreateStyleElementsForAssets({
      beginStylesMeta,
      editorGeneratedStylesElement,
      cssAssets
    });

    // Remove all the old style elements once the new style elements
    // finish loading, and give the browser a second to parse / apply the css
    const thisRenderId = this.currentRenderId + 1;
    this.currentRenderId = thisRenderId;
    this.currentRenderPromiseForTest = Promise.all(newStyleLoadPromises).then(() => {
      return new Promise(resolve => {
        setTimeout(() => resolve(true), 1000);
      });
    }).then(() => {
      // So long as the renderId hasn't in the time between
      // this method was originally called and _now_,
      // remove the old elements. Otherwise the follow
      // up calls to this method will handle removal
      if (this.currentRenderId === thisRenderId) {
        oldStyleElementsToRemove.forEach(existingStyleElement => {
          existingStyleElement.remove();
        });
        clonedAndMovedStyleElementsToRemove.forEach(existingStyleElement => {
          existingStyleElement.remove();
        });
      }
    });
    if (window.SELENIUM_IS_RUNNING) {
      dispatchActionToEventBus(logInpageSeleniumEvent('cssUpdatedOnPage'));
    }
  }
  render() {
    return null;
  }
}
const mapStateToProps = state => {
  return {
    cssAssets: getContentCssAssets(state)
  };
};
export default connect(mapStateToProps)(ContentCssAssetContainer);